import { Link } from "react-router-dom";
import { getCampaigns } from "../../apis/campaign";
import {
    normalizeCampaignsResponse,
    getCampaignName,
    getCampaignDate,
    getDisplayPlatform,
    getDisplayLanguage,
    getCampaignPlatformIcon,
    isCampaignDraft,
} from "../../utils/normalizeCampaign";
import { useCursorPaginatedList, mergeById } from "../../hooks/useCursorPaginatedList";
import { useUrlListFilters } from "../../hooks/useUrlListFilters";
import FilterBar from "../shared/FilterBar";
import ListScrollFooter from "../shared/ListScrollFooter";
import EmptyListState from "../shared/EmptyListState";
import PageHeader, { PageShell } from "../shared/PageHeader";
import { CardList, CARD_ITEM_PADDING_CLASS } from "../shared/ContentList";
import CampaignListSkeletons from "./CampaignListSkeletons";
import "bootstrap-icons/font/bootstrap-icons.css";
import { APP_ROUTES, buildCampaignPath, buildCampaignWizardPath } from "../../constants/appRoutes";

const LOAD_MORE_SKELETON_COUNT = 3;

function formatCampaignDate(value) {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
}

function CampaignListCard({ campaign }) {
    const id = campaign.id;
    const createdLabel = formatCampaignDate(getCampaignDate(campaign));
    const platform = getDisplayPlatform(campaign);
    const language = getDisplayLanguage(campaign);
    const platformIcon = getCampaignPlatformIcon(campaign);
    const isDraft = isCampaignDraft(campaign);

    return (
        <article
            className={`campaign-box w-full min-w-0 max-w-full overflow-hidden ${CARD_ITEM_PADDING_CLASS}`}
        >
            <div className="flex min-w-0 items-center justify-between gap-3">
                <Link
                    to={buildCampaignPath(id)}
                    className="min-w-0 flex-1 no-underline text-inherit transition hover:text-slate-900"
                >
                    <p className="mb-1 break-words font-semibold text-slate-900">
                        {getCampaignName(campaign)}
                    </p>
                    <div className="flex min-w-0 flex-wrap items-center gap-2 text-xs text-slate-500 md:text-sm">
                        {platform ? (
                            <span className="inline-flex items-center gap-1">
                                <i
                                    className={`bi ${platformIcon.icon} ${platformIcon.iconClass}`}
                                    aria-hidden
                                />
                                {platform}
                            </span>
                        ) : null}
                        {platform && createdLabel ? (
                            <span className="text-slate-300" aria-hidden>
                                ·
                            </span>
                        ) : null}
                        {createdLabel ? (
                            <span className="inline-flex items-center gap-1">
                                <i className="bi bi-calendar3" aria-hidden />
                                {createdLabel}
                            </span>
                        ) : null}
                        {(platform || createdLabel) && language ? (
                            <span className="text-slate-300" aria-hidden>
                                ·
                            </span>
                        ) : null}
                        {language ? (
                            <span className="inline-flex items-center gap-1">
                                <i className="bi bi-translate" aria-hidden />
                                {language}
                            </span>
                        ) : null}
                    </div>
                </Link>

                <div className="flex shrink-0 items-center gap-2">
                    {isDraft ? (
                        <Link
                            to={buildCampaignWizardPath(null, id)}
                            className="btn btn-dark-app px-3 py-1.5 text-xs font-semibold no-underline md:px-4 md:py-2 md:text-sm"
                        >
                            <i className="bi bi-arrow-right-circle me-1.5" aria-hidden />
                            Continue
                        </Link>
                    ) : null}
                    <Link
                        to={buildCampaignPath(id)}
                        className="text-slate-400 transition hover:text-slate-600"
                        aria-label={`View ${getCampaignName(campaign)}`}
                    >
                        <i className="bi bi-chevron-right" aria-hidden />
                    </Link>
                </div>
            </div>
        </article>
    );
}

function applyClientFilters(campaigns, { startDate, endDate }) {
    let filtered = campaigns;

    if (startDate) {
        const from = new Date(startDate);
        filtered = filtered.filter((campaign) => {
            const created = getCampaignDate(campaign);
            return created && new Date(created) >= from;
        });
    }

    if (endDate) {
        const to = new Date(endDate);
        to.setHours(23, 59, 59, 999);
        filtered = filtered.filter((campaign) => {
            const created = getCampaignDate(campaign);
            return created && new Date(created) <= to;
        });
    }

    return filtered;
}

export default function MyCampaigns() {
    const {
        limit,
        startDate,
        endDate,
        setLimit,
        setStartDate,
        setEndDate,
    } = useUrlListFilters();

    const {
        items: campaigns,
        hasMore,
        loading,
        loadingMore,
        error,
        loadMoreRef,
    } = useCursorPaginatedList({
        fetchFn: getCampaigns,
        normalizeResponse: normalizeCampaignsResponse,
        applyClientFilters,
        mergeItems: mergeById,
        filters: { limit, startDate, endDate },
    });

    return (
        <PageShell>
            <PageHeader
                title="My Campaigns"
                description="View and manage your saved campaigns."
            />

            <FilterBar
                limit={limit}
                onLimitChange={setLimit}
                startDate={startDate}
                onStartDateChange={setStartDate}
                endDate={endDate}
                onEndDateChange={setEndDate}
            />

            {error ? (
                <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700">
                    <p className="mb-0 font-semibold">Could not load campaigns</p>
                    <p className="mt-1 text-sm">{error}</p>
                </div>
            ) : null}

            {loading ? (
                <CampaignListSkeletons count={limit} />
            ) : campaigns.length === 0 ? (
                <EmptyListState
                    message={
                        startDate || endDate
                            ? "No campaigns match your filters."
                            : "No campaigns to show yet."
                    }
                    actionLabel={
                        !startDate && !endDate
                            ? "Create your first campaign"
                            : undefined
                    }
                    actionTo={APP_ROUTES.CAMPAIGN_WIZARD}
                />
            ) : (
                <CardList>
                    {campaigns.map((campaign, index) => (
                        <CampaignListCard
                            key={campaign.id ?? campaign._id ?? index}
                            campaign={campaign}
                        />
                    ))}
                </CardList>
            )}

            {loadingMore ? (
                <CampaignListSkeletons
                    count={LOAD_MORE_SKELETON_COUNT}
                    label="Loading more campaigns"
                    className="mt-3"
                />
            ) : null}

            <ListScrollFooter
                loadMoreRef={loadMoreRef}
                showAllShown={!loading && !loadingMore && campaigns.length > 0 && !hasMore}
            />
        </PageShell>
    );
}
